import { useQuery } from "@tanstack/react-query";
import { FormEvent, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import Button from "@/components/Button";
import { FormLayout, Label } from "@/components/Forms";
import { NewDropdown } from "@/components/Forms/Dropdown";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import Text from "@/components/Text";
import useAlerts from "@/hooks/useAlerts";
import { selectTeamTestingProperties } from "@/selectors";
import { api } from "@/utils/api";
import { handleFormErrors } from "@/utils/helpers";
import { RootState, Dispatch } from "@/utils/store";

type FormDataT = {
  google_ads_client_id?: string;
};

const Card = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
`;

const HeadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: solid 1px ${({ theme }) => theme.colors.grayscaleLight};
`;

const Content = styled.section`
  padding: 24px;
`;

const Form = styled.form`
  margin-top: 24px;
`;

const TestingYoutubeProperties = () => {
  const { teamId } = useParams();

  const alerts = useAlerts();

  const dispatch = useDispatch<Dispatch>();

  const { data: googleAdsAccounts } = useQuery<{ id: number; name: string }[]>(
    ["google_ads_channels"],
    () => api.get<never, { id: number; name: string }[]>("/v1/ga-accounts"),
    {
      staleTime: 120 * 1000,
    }
  );

  const { testingSettings } = useSelector((state: RootState) =>
    selectTeamTestingProperties(state, teamId!)
  );

  const {
    handleSubmit,
    clearErrors,
    setError,
    setValue,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormDataT>();

  useEffect(() => {
    const currentGoogleAccount = googleAdsAccounts?.find(
      (account) =>
        account.id.toString() ===
        testingSettings?.google_ads_client_id?.toString()
    );
    currentGoogleAccount &&
      setValue("google_ads_client_id", currentGoogleAccount.id.toString());
  }, [googleAdsAccounts, setValue, testingSettings?.google_ads_client_id]);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        const properties = await api.post(
          `/v1/teams/${teamId}/testing-settings/properties`,
          {
            ...data,
            network: "youtube",
          }
        );

        dispatch.teams.updateTeamTestingProperties({
          properties,
          teamId: parseInt(teamId!),
        });

        alerts.success("Youtube testing properties saved");
      } catch (err) {
        handleFormErrors(err, setError, alerts);
      }
    })(e);
  };

  return (
    <Card>
      <Form onSubmit={onSubmit}>
        <HeadingRow>
          <div>
            <Text $type="caption" as="h3">
              Youtube optimisation testing properties
            </Text>
          </div>
          <div>
            <Button
              $type="colorized"
              disabled={isSubmitting || !isDirty}
              loading={isSubmitting}
            >
              Save
            </Button>
          </div>
        </HeadingRow>

        <Content>
          <FormLayout>
            <div>
              <Label htmlFor="googleAccountsSelect">Google ads account</Label>
              <NewDropdown<string>
                options={
                  googleAdsAccounts?.map((account) => {
                    return {
                      label: account.name,
                      value: account.id.toString(),
                    };
                  }) ?? []
                }
                control={control}
                name="google_ads_client_id"
                id="googleAccountsSelect"
              />
              {!!errors.google_ads_client_id && (
                <ValidationMessage>
                  {errors.google_ads_client_id.message}
                </ValidationMessage>
              )}
            </div>
          </FormLayout>
        </Content>
      </Form>
    </Card>
  );
};

export default TestingYoutubeProperties;
